import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStoryStore } from "@/application/stores/storyStore";
import { useUiStore } from "@/application/stores/uiStore";
import type { Chapter } from "@/domain/story/model/chapter";
import type { Emphasis, Line } from "@/domain/story/model/line";
import type { Scene } from "@/domain/story/model/scene";
import { playVoice, stopVoice } from "@/infrastructure/assets/audio";
import {
  advVoiceAssetPath,
  type BackgroundName,
  type BgmName,
  backgroundAssetPath,
  type CompanionExpression,
  companionAssetPath,
  type VoiceChapterKind,
} from "@/infrastructure/assets/paths";
import { Button } from "@/presentation/components/Button";
import { useBgm } from "@/presentation/features/bgm/useBgm";
import "./AdvScene.css";

/** 語りの送り速度（ui-design.md §7.2「送りは20ms/字」）。 */
const REVEAL_INTERVAL_MS = 20;

/**
 * 立ち絵クロスフェードの長さ。CSS側は `var(--duration-1)`（120ms）を使うが、
 * 古い表情の層をDOMから取り除くタイミングをJSのタイマーでも把握する必要があるため、
 * トークンと同じ値をここに複製している（ui-design.md §8「表情の差し替え: 120ms」）。
 */
const COMPANION_CROSSFADE_MS = 120;

export interface AdvSceneProps {
  readonly chapter: Chapter;
  /** 最後のシーンの最後のセリフを送った時、または「飛ばす」を押した時に一度だけ呼ぶ。 */
  readonly onComplete: () => void;
}

interface TextSegment {
  readonly key: string;
  readonly text: string;
  readonly emphasized: boolean;
  readonly revealed: boolean;
}

interface CompanionLayer {
  readonly id: number;
  readonly expression: string;
}

/**
 * `prefers-reduced-motion`を購読する。jsdom（テスト環境）は`window.matchMedia`を
 * 実装していないため、未実装環境では「動きを抑えない」側にフォールバックする。
 */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mediaQuery.matches);
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

/**
 * `Line.emphasis`（文字位置の数値範囲。NFR-004によりHTML文字列は持たない）と
 * 送りの`revealedCount`から、描画すべきセグメント列を組み立てる。
 *
 * 全文は常にDOMに存在させる（アクセシビリティ要件：語りは送りの途中でも読み上げが
 * 全文を取得できる、ui-design.md §9）。未送り分を`display:none`や`visibility:hidden`
 * ではなく`opacity:0`のみで隠すことで、アクセシビリティツリーからは消さない。
 */
function buildSegments(
  text: string,
  emphasis: readonly Emphasis[],
  revealedCount: number,
): TextSegment[] {
  const length = text.length;
  const clamp = (value: number) => Math.min(Math.max(value, 0), length);
  const boundaries = new Set<number>([0, length, clamp(revealedCount)]);
  for (const range of emphasis) {
    boundaries.add(clamp(range.start));
    boundaries.add(clamp(range.end));
  }
  const sorted = [...boundaries].sort((a, b) => a - b);

  const segments: TextSegment[] = [];
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const start = sorted[i];
    const end = sorted[i + 1];
    if (start === undefined || end === undefined || end <= start) {
      continue;
    }
    const emphasized = emphasis.some((range) => range.start <= start && end <= range.end);
    segments.push({
      key: `${start}-${end}`,
      text: text.slice(start, end),
      emphasized,
      revealed: start < revealedCount,
    });
  }
  return segments;
}

/**
 * 稽古前の話 / 稽古後の話（V-1・V-5）。1つの`Chapter`（シーン×セリフの列）を
 * 全画面没入のビジュアルノベル風に描く、プレゼンテーションのみのコンポーネント。
 *
 * 位置づけ（screen-flow.md §7.2）: Z1（帳場）はこの画面では隠れる想定のため、
 * ここでは描かない（呼び出し側の責務）。本コンポーネントはZ2（背景+立ち絵+
 * セリフウィンドウ）に加えて、「話」の体験の一部であるZ3の進み具合の点も
 * 内包する（シーン単位の点は語り自体に付随する情報であり、分離しても呼び出し側で
 * 別途チャプター位置を渡し直すだけになるため、ここに含める判断とした）。
 * 段の状態機械（V-1〜V-5の遷移そのもの）・帳場・師範代の浮遊表示（Z4、V-1/V-5には
 * 出ない）は本コンポーネントの外側の責務。
 */
export function AdvScene({ chapter, onComplete }: AdvSceneProps) {
  const sceneIndex = useStoryStore((state) => state.sceneIndex);
  const lineIndex = useStoryStore((state) => state.lineIndex);
  const setChapterId = useStoryStore((state) => state.setChapterId);
  const setSceneIndex = useStoryStore((state) => state.setSceneIndex);
  const setLineIndex = useStoryStore((state) => state.setLineIndex);
  const setCompanionExpression = useStoryStore((state) => state.setCompanionExpression);

  const completedRef = useRef(false);

  // マウント時、および章が変わった時に位置を先頭へ戻す。
  useEffect(() => {
    completedRef.current = false;
    setChapterId(chapter.id);
    setSceneIndex(0);
    setLineIndex(0);
  }, [chapter.id, setChapterId, setSceneIndex, setLineIndex]);

  // ストア上の位置がまだ前の章のものである可能性がある（上のリセットのuseEffectは
  // 描画後に走るため）。範囲外アクセスによるクラッシュを避けるための安全な添字。
  // `chapter.scenes`/`Scene.lines`はZodスキーマが`.min(1)`で非空を強制済みのため、
  // `noUncheckedIndexedAccess`が要求する`undefined`分岐は現実には起こらない（安全なキャスト）。
  const safeSceneIndex = sceneIndex < chapter.scenes.length ? sceneIndex : 0;
  const currentScene = (chapter.scenes[safeSceneIndex] ?? chapter.scenes[0]) as Scene;
  const safeLineIndex = lineIndex < currentScene.lines.length ? lineIndex : 0;
  const currentLine = currentScene.lines[safeLineIndex] as Line;

  const muted = useUiStore((state) => state.muted);
  const volume = useUiStore((state) => state.volume);
  const bgmName = currentScene.bgmPath as BgmName;

  // 囃子はシーンのbgmPathに従う（useBgm。ミュート中は音源を取得しない）。
  useBgm(bgmName);

  // 師範代ボイス: セリフが変わるたびに再生。ミュート中は fetch しない。
  // 再生開始は次タスクへ逃がし、遷移直後の描画（NFR-001）と同期経路を共有しない
  // （打ち音と同じく setTimeout(0)。ui-design / NFR-002 の教訓）。
  useEffect(() => {
    if (muted) {
      stopVoice();
      return undefined;
    }
    const src = advVoiceAssetPath(
      chapter.levelId,
      chapter.kind as VoiceChapterKind,
      safeSceneIndex,
      safeLineIndex,
    );
    const timer = window.setTimeout(() => {
      playVoice(src, false, volume);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      stopVoice();
    };
  }, [chapter.levelId, chapter.kind, safeSceneIndex, safeLineIndex, muted, volume]);

  useEffect(() => stopVoice, []);

  const osReducedMotion = usePrefersReducedMotion();
  const reduceMotionOverride = useUiStore((state) => state.reduceMotionOverride);
  const reducedMotion = osReducedMotion || reduceMotionOverride;
  const narrationSpeedMultiplier = useUiStore((state) => state.narrationSpeedMultiplier);
  const revealIntervalMs = REVEAL_INTERVAL_MS / narrationSpeedMultiplier;

  const [revealedCount, setRevealedCount] = useState(() =>
    reducedMotion ? currentLine.text.length : 0,
  );

  // セリフが変わるたびに送りをやり直す。動きを抑える設定では即時に全文を出す。
  useEffect(() => {
    setRevealedCount(reducedMotion ? currentLine.text.length : 0);
  }, [currentLine, reducedMotion]);

  // 1字ずつ送る。動きを抑える設定、または送り終わっている間はタイマーを張らない。
  useEffect(() => {
    if (reducedMotion || revealedCount >= currentLine.text.length) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setRevealedCount((count) => Math.min(count + 1, currentLine.text.length));
    }, revealIntervalMs);
    return () => window.clearTimeout(timer);
  }, [currentLine, reducedMotion, revealedCount, revealIntervalMs]);

  // 表情をストアへ同期する（他のビューが相棒の表情を参照する余地を残すため）。
  useEffect(() => {
    setCompanionExpression(currentLine.expression);
  }, [currentLine, setCompanionExpression]);

  const layerIdRef = useRef(0);
  const [companionLayers, setCompanionLayers] = useState<CompanionLayer[]>(() => [
    { id: layerIdRef.current, expression: currentLine.expression },
  ]);

  // 表情が変わったら新しい層を積む（位置・大きさは動かさず、表情だけ差し替える。
  // ui-design.md §5.7）。常に直近の1層+新しい1層の最大2層に保つ。
  useEffect(() => {
    setCompanionLayers((layers) => {
      const top = layers.at(-1);
      if (top?.expression === currentLine.expression) {
        return layers;
      }
      layerIdRef.current += 1;
      return [...layers.slice(-1), { id: layerIdRef.current, expression: currentLine.expression }];
    });
  }, [currentLine]);

  // クロスフェードが終わったら古い層を取り除く。動きを抑える設定では即座に。
  useEffect(() => {
    if (companionLayers.length <= 1) {
      return undefined;
    }
    if (reducedMotion) {
      setCompanionLayers((layers) => layers.slice(-1));
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setCompanionLayers((layers) => layers.slice(-1));
    }, COMPANION_CROSSFADE_MS);
    return () => window.clearTimeout(timer);
  }, [companionLayers, reducedMotion]);

  const isFullyRevealed = revealedCount >= currentLine.text.length;
  const isLastLineOfScene = safeLineIndex >= currentScene.lines.length - 1;
  const isLastScene = safeSceneIndex >= chapter.scenes.length - 1;

  const complete = useCallback(() => {
    if (completedRef.current) {
      return;
    }
    completedRef.current = true;
    stopVoice();
    onComplete();
  }, [onComplete]);

  const handleAdvance = useCallback(() => {
    if (!isFullyRevealed) {
      setRevealedCount(currentLine.text.length);
      return;
    }
    stopVoice();
    if (!isLastLineOfScene) {
      setLineIndex(safeLineIndex + 1);
      return;
    }
    if (!isLastScene) {
      setSceneIndex(safeSceneIndex + 1);
      setLineIndex(0);
      return;
    }
    complete();
  }, [
    isFullyRevealed,
    isLastLineOfScene,
    isLastScene,
    currentLine,
    safeLineIndex,
    safeSceneIndex,
    setLineIndex,
    setSceneIndex,
    complete,
  ]);

  const segments = useMemo(
    () => buildSegments(currentLine.text, currentLine.emphasis, revealedCount),
    [currentLine, revealedCount],
  );

  // どこにフォーカスがあってもEnterで送り/次へ進める（「飛ばす」にフォーカスがある場合も
  // 段階的な送りを優先する）。モーダルが開いている間はそちら優先のため何もしない。
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Enter") {
        return;
      }
      if (document.activeElement?.closest(".modal-panel")) {
        return;
      }
      event.preventDefault();
      handleAdvance();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleAdvance]);

  return (
    <div className="adv-scene">
      {/* 背景は情景のみを担う装飾（ui-design.md §6.4）。装飾のため読み上げからは隠す。 */}
      <img
        className="adv-scene__background"
        src={
          // upstreamのZodスキーマ（BACKGROUND_NAMES enum）で検証済みのため安全にキャストする。
          backgroundAssetPath(currentScene.backgroundPath as BackgroundName)
        }
        alt=""
        aria-hidden="true"
      />
      <div className="adv-scene__scrim" aria-hidden="true" />

      <Button variant="quiet" className="adv-scene__skip" onClick={complete}>
        飛ばす
      </Button>

      {/* 師範代ノーマの立ち絵。位置・大きさは固定、表情のみ差し替える（ui-design.md §5.7）。
          話者名は既にテキストで伝えているため装飾扱い。 */}
      <div className="adv-scene__companion" aria-hidden="true">
        {companionLayers.map((layer, index) => (
          <img
            key={layer.id}
            className={[
              "adv-scene__companion-img",
              index > 0 ? "adv-scene__companion-img--enter" : null,
            ]
              .filter(Boolean)
              .join(" ")}
            src={
              // upstreamのZodスキーマ（COMPANION_EXPRESSIONS enum）で検証済みのため安全にキャストする。
              companionAssetPath(layer.expression as CompanionExpression)
            }
            alt=""
          />
        ))}
      </div>

      <div className="adv-scene__dialogue">
        <button type="button" className="adv-scene__window" onClick={handleAdvance}>
          <p className="adv-scene__speaker">{currentLine.speaker}</p>
          <p className="adv-scene__text">
            {segments.map((segment) => {
              const Tag = segment.emphasized ? "strong" : "span";
              return (
                <Tag
                  key={segment.key}
                  className={segment.emphasized ? "adv-scene__text-emphasis" : undefined}
                  style={segment.revealed ? undefined : { opacity: 0 }}
                >
                  {segment.text}
                </Tag>
              );
            })}
          </p>
          <p className="adv-scene__hint">{isFullyRevealed ? "次へ ⏎ / Space" : "送る ⏎ / Space"}</p>
        </button>
      </div>

      {/* 進み具合の点（screen-flow.md §7.2 Z3）。Sceneには一意なIDが無いため、
          並び替えの起きない配列のindexをkeyに使う（順序は`chapter.scenes`の並びで固定）。 */}
      <div className="adv-scene__dots" aria-hidden="true">
        {chapter.scenes.map((_scene, index) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: Sceneに一意なIDが無く、順序も不変のため。
            key={index}
            className={[
              "adv-scene__dot",
              index === safeSceneIndex ? "adv-scene__dot--current" : null,
            ]
              .filter(Boolean)
              .join(" ")}
          />
        ))}
      </div>
    </div>
  );
}
